import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/api";
import { toPng } from "html-to-image";

// Sanitize URL to remove duplicate base URLs
const sanitizeUrl = (url: string): string => {
  if (!url) return url;

  // Match pattern: https://domain.com/https://domain.com/path
  const duplicatePattern = /^(https?:\/\/[^/]+)\/(https?:\/\/)/;
  const match = url.match(duplicatePattern);

  if (match) {
    // Remove the first base URL and leading slash, keep the second full URL
    return url.replace(duplicatePattern, "$2");
  }

  return url;
};

type ScreenshotParams = {
  project: string;
  module: string;
  section?: string;
  page?: string;
};

type ScreenshotData = {
  _id: string;
  project: string;
  module: string;
  section?: string;
  page?: string;
  screenshot: string;
};

type UseChartScreenshotReturn = {
  containerRef: React.RefObject<HTMLDivElement>;
  screenshotUrl: string | null;
  isLoading: boolean;
  isCaptured: boolean;
  captureAndUpload: () => Promise<void>;
};

const useChartScreenshot = (
  params: ScreenshotParams,
  enabled = true
): UseChartScreenshotReturn => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [existingScreenshot, setExistingScreenshot] =
    useState<ScreenshotData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCaptured, setIsCaptured] = useState(false);
  const hasFetchedRef = useRef(false);
  const hasUploadedRef = useRef(false);

  const { project, module, section, page } = params;

  // Fetch existing screenshot
  useEffect(() => {
    if (!enabled || !project || !module || hasFetchedRef.current) return;

    const fetchScreenshot = async () => {
      try {
        hasFetchedRef.current = true;
        const queryParams = new URLSearchParams({
          project,
          module,
        });
        if (section) queryParams.append("section", section);
        if (page) queryParams.append("page", page);

        const response = await api.get(`/screenshot/get?${queryParams}`);
        const data = response.data?.data;

        if (data && data.length > 0) {
          // Find the most relevant screenshot
          const screenshot = data.find(
            (s: ScreenshotData) =>
              s.module === module &&
              (!section || s.section === section) &&
              (!page || s.page === page)
          );

          if (screenshot) {
            setExistingScreenshot(screenshot);
            setScreenshotUrl(sanitizeUrl(screenshot.screenshot));
            setIsCaptured(true);
          }
        }
      } catch (error) {
        // No existing screenshot, will capture new one
      } finally {
        setIsLoading(false);
      }
    };

    fetchScreenshot();
  }, [enabled, project, module, section, page]);

  // Capture screenshot from container
  const captureScreenshot = useCallback(async (): Promise<Blob | null> => {
    if (!containerRef.current) return null;

    try {
      const dataUrl = await toPng(containerRef.current, {
        cacheBust: true,
        backgroundColor: "#ffffff",
        pixelRatio: 2,
      });

      // Convert data URL to Blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error("Error capturing screenshot:", error);
      return null;
    }
  }, []);

  // Upload or update screenshot
  const captureAndUpload = useCallback(async () => {
    if (!enabled || !project || !module || hasUploadedRef.current) return;

    const blob = await captureScreenshot();
    if (!blob) return;

    hasUploadedRef.current = true;

    const formData = new FormData();
    formData.append(
      "screenshot",
      blob,
      `screenshot-${module}-${Date.now()}.png`
    );
    formData.append("project", project);
    formData.append("module", module);
    if (section) formData.append("section", section);
    if (page) formData.append("page", page);

    try {
      let response;

      if (existingScreenshot?._id) {
        // Update existing screenshot
        response = await api.put(
          `/screenshot/update/${existingScreenshot._id}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      } else {
        // Create new screenshot
        response = await api.post("/screenshot/create", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      const newScreenshot = response.data?.data;
      if (newScreenshot?.screenshot) {
        setScreenshotUrl(sanitizeUrl(newScreenshot.screenshot));
        setExistingScreenshot(newScreenshot);
      }
      setIsCaptured(true);
    } catch (error) {
      console.error("Error uploading screenshot:", error);
      hasUploadedRef.current = false;
    }
  }, [enabled, project, module, section, page, existingScreenshot, captureScreenshot]);

  return {
    containerRef,
    screenshotUrl,
    isLoading,
    isCaptured,
    captureAndUpload,
  };
};

export default useChartScreenshot;
